import React from 'react';
import { MEEM_BLOCKS } from '../../lib/meemItems';
import { itemKey } from '../../lib/guidedFlow';
import PentagonFigure from './PentagonFigure';

const fmt = (n) => String(n).replace('.', ',');

const CheckIcon = () => (
  <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"><path d="M4 10.5l4 4 8-9" /></svg>
);
const CrossIcon = () => (
  <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round"><path d="M5 5l10 10M15 5L5 15" /></svg>
);

/** Id do elemento do item, usado pela régua e pelo "Ir ao primeiro" para rolar até ele. */
export const itemDomId = (blockId, index) => `item-${blockId}-${index}`;

/**
 * Destaca a palavra de lateralidade (direita/esquerda) em peso 600 sem mudar o texto clínico.
 * (Hoje nenhum item ou nota do protocolo usa essas palavras; o destaque vale se algum passar a usar.)
 */
export function destacarLateralidade(text) {
  return String(text).split(/(direita|esquerda)/gi).map((part, i) => (
    /^(direita|esquerda)$/i.test(part) ? <strong key={i} className="font-semibold text-[var(--ink)]">{part}</strong> : part
  ));
}

/**
 * Um item: dois botões num só controle ([certo 1][errado 0]), com aria-pressed.
 * Marcado = só o segmento escolhido ganha tinta (acerto: acento a 12% + texto acento; erro: terracota a 10%);
 * o fundo do bloco continua branco, então o escore é o que mais chama atenção. Clicar de novo no ativo
 * volta a "não avaliado". Teclado: 1 = acertou, 0 = errou, setas movem entre os dois, Backspace/Delete limpam.
 * Itens marcados em lote mostram "em lote" até serem tocados.
 */
function ItemRow({ block, item, index, value, inBatch, onAnswer }) {
  const key = itemKey(block.id, index);
  const labelId = `resp-${block.id}-${index}-rotulo`;

  const onKeyDown = (event) => {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.key === '1' || event.key === '0') {
      event.preventDefault();
      const want = event.key === '1';
      onAnswer(key, value === want ? null : want);
    } else if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault();
      onAnswer(key, null);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      const buttons = Array.from(event.currentTarget.querySelectorAll('button'));
      const at = buttons.indexOf(document.activeElement);
      if (at !== -1) { event.preventDefault(); buttons[(at + (event.key === 'ArrowRight' ? 1 : buttons.length - 1)) % buttons.length].focus(); }
    }
  };

  const segment = (want) => {
    const pressed = value === want;
    const base = 'relative flex h-12 w-[68px] select-none items-center justify-center gap-1.5 border text-[length:var(--fs-15)] font-semibold transition-colors duration-[120ms] ease-out focus-visible:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]';
    const shape = want ? 'rounded-l-[6px]' : '-ml-px rounded-r-[6px]';
    const tone = pressed
      ? (want ? 'z-[1] border-[var(--accent)] bg-[var(--accent-tint)] text-[var(--accent)]' : 'z-[1] border-[var(--miss)] bg-[var(--miss-tint)] text-[var(--miss)]')
      : 'border-[var(--cell-border)] bg-[var(--surface)] text-[var(--ink)] hover:bg-[var(--hover)]';
    return (
      <button
        type="button"
        aria-pressed={pressed}
        aria-label={want ? 'Acertou, 1 ponto' : 'Errou, 0 pontos'}
        onClick={() => onAnswer(key, pressed ? null : want)}
        className={`${base} ${shape} ${tone}`}
      >
        {want ? <CheckIcon /> : <CrossIcon />}
        <span aria-hidden="true">{want ? '1' : '0'}</span>
      </button>
    );
  };

  return (
    <li id={itemDomId(block.id, index)} className="scroll-mt-32">
      <div
        role="group"
        aria-labelledby={labelId}
        onKeyDown={onKeyDown}
        className="flex min-h-14 items-center justify-between gap-3 rounded-[6px] py-2 sm:gap-6"
      >
        <div className="min-w-0">
          <span id={labelId} className="block text-[length:var(--fs-16)] font-medium leading-snug text-[var(--ink)]">
            {destacarLateralidade(item.label)}
            {inBatch && (
              <span className="ml-2 inline-flex items-center gap-1 align-middle text-[length:var(--fs-13)] font-normal text-[var(--ink-2)]">
                <span aria-hidden="true" className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--cut)]" />
                em lote<span className="sr-only"> (marcado junto com o bloco, ainda não conferido)</span>
              </span>
            )}
          </span>
          <span className="block text-[length:var(--fs-14)] leading-snug text-[var(--ink-2)]">{destacarLateralidade(item.criterio)}</span>
        </div>
        <div className="flex shrink-0">
          {segment(true)}
          {segment(false)}
        </div>
      </div>
    </li>
  );
}

/**
 * Um bloco do protocolo: seção com divisória, dentro do painel branco da calculadora.
 * Título em Geist 600, frase normal (a fração n/max é a única parte em Geist Mono). "Marcar bloco como
 * correto (n)" é um botão secundário VISÍVEL, só deste bloco, com Desfazer por 8s.
 */
export default function BlockSection({ block, checked, batch = {}, onAnswer, onBatch, undo = false, onUndo }) {
  const acertos = block.items.filter((_, i) => checked[itemKey(block.id, i)] === true).length;
  const index = MEEM_BLOCKS.findIndex((b) => b.id === block.id) + 1;
  const headingId = `bloco-${block.id}-titulo`;
  const multi = block.items.length > 1;

  return (
    <section id={`bloco-${block.id}`} aria-labelledby={headingId} className="scroll-mt-32 rounded-[8px] border border-[var(--line)] bg-[var(--surface)] px-4 pb-2 pt-4 md:px-6 md:pt-5">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <h3 id={headingId} className="flex items-baseline gap-2.5 font-sans text-[length:var(--fs-17)] font-semibold leading-snug">
          <span aria-hidden="true" className="tabular text-[length:var(--fs-13)] font-semibold text-[var(--accent)]">{String(index).padStart(2, '0')}</span>
          <span>{block.label} <span className="text-[var(--ink-2)]" aria-hidden="true">·</span> <span className="mono font-normal text-[var(--ink-2)]">{acertos}/{block.max}</span></span>
        </h3>
        {multi && (undo ? (
          <p role="status" className="flex shrink-0 items-center gap-1 text-[length:var(--fs-14)] text-[var(--ink-2)]">
            Bloco marcado
            <button type="button" onClick={onUndo} className="min-h-11 rounded-[6px] px-2 font-semibold text-[var(--accent)] underline">Desfazer</button>
          </p>
        ) : (
          <button
            type="button"
            onClick={() => onBatch(block.id)}
            className="inline-flex min-h-11 shrink-0 items-center text-[length:var(--fs-13)] font-medium text-[var(--ink-2)] underline underline-offset-4 hover:text-[var(--accent)]"
          >
            Marcar bloco como correto ({block.items.length})
          </button>
        ))}
      </div>
      <span className="sr-only">Bloco {index} de {MEEM_BLOCKS.length}.</span>

      <details className="mt-1">
        <summary className="inline-flex min-h-11 cursor-pointer items-center text-[length:var(--fs-14)] text-[var(--ink-2)] underline">Como aplicar</summary>
        <p className="pb-2 pt-1 text-[length:var(--fs-15)] leading-relaxed text-[var(--ink-2)]">{destacarLateralidade(block.nota)}</p>
        <p className="pb-3 text-[length:var(--fs-14)] leading-relaxed text-[var(--ink-2)]">
          <strong className="font-semibold text-[var(--ink)]">Dados normativos deste bloco.</strong>{' '}
          Em pessoas saudáveis, metade pontuou {fmt(block.normativo.mediana)} ou menos neste bloco, e a menor pontuação registrada foi {block.normativo.minObservado} de {block.max}. Média {fmt(block.normativo.media)}, com dispersão de {fmt(block.normativo.dp)} ponto{block.normativo.dp === 1 ? '' : 's'} em torno dela. Amostra de 185 pessoas.
        </p>
      </details>

      <ul>
        {block.items.map((item, i) => (
          <ItemRow
            key={itemKey(block.id, i)}
            block={block}
            item={item}
            index={i}
            value={checked[itemKey(block.id, i)]}
            inBatch={Boolean(batch[itemKey(block.id, i)])}
            onAnswer={onAnswer}
          />
        ))}
      </ul>

      {block.figura && <PentagonFigure />}
    </section>
  );
}
